import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, take, takeUntil } from 'rxjs';
import { AnalysisReportService, LocationResult } from '../services/analysis-report.service';

type SortColumn = 'location' | 'meetsQualityStandards' | 'score' | 'analysis';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-analysis-report-questions-details',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './analysis-report-questions-details.component.html',
  styleUrl: './analysis-report-questions-details.component.css',
  standalone: true
})
export class AnalysisReportQuestionsDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  currentTheme: 'light' | 'dark' = 'light';
  isLoading: boolean = false;
  templateId: string = '';
  templateName: string = '';
  questionId: string = '';
  questionName: string = '';
  questionPrompts: string[] = [];
  confidenceScore: number = 0;
  locationResults: LocationResult[] = [];
  selectedImageUrl: string | null = null;
  selectedItem: LocationResult | null = null;
  searchQuery: string = '';
  sortColumn: SortColumn = 'location';
  sortDirection: SortDirection = 'asc';
  detailImageSrc: string | null = null;
  fullImageSrc: string | null = null;
  private detailImageRetries = 0;
  private fullImageRetries = 0;
  private readonly maxImageRetries = 4;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analysisReportService: AnalysisReportService
  ) {}

  ngOnInit() {
    this.initializeTheme();

    // Get templateId and questionId from navigation state (internal SPA navigation)
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;

    if (state?.templateId && state?.questionId) {
      this.templateId = state.templateId;
      this.questionId = state.questionId;
      this.templateName = state.templateName || '';
      this.questionName = state.questionName || '';
      this.questionPrompts = this.parsePrompts(state.questionPrompts);
      this.loadQuestionDetails();
      return;
    }

    // Fallback to query params for direct/external links (e.g. from creator UI)
    this.route.queryParams
      .pipe(take(1))
      .subscribe(params => {
        const templateId = params['templateId'] || params['templateid'];
        const questionId = params['questionId'] || params['questionid'];
        if (templateId && questionId) {
          this.templateId = templateId;
          this.questionId = questionId;
          this.templateName = params['templateName'] || '';
          this.questionName = params['questionName'] || '';
          this.loadQuestionDetails();
        } else {
          // Redirect back if no IDs provided
          this.router.navigate(['/analysis-report']);
        }
      });
  }

  ngAfterViewInit() {
    this.initializeLucideIcons();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private parsePrompts(prompts: string | string[] | undefined): string[] {
    if (!prompts) return [];
    if (Array.isArray(prompts)) return prompts;
    return prompts.split(',').map(p => p.trim()).filter(p => p.length > 0);
  }

  loadQuestionDetails() {
    this.isLoading = true;
    this.analysisReportService.getQuestionDetails(this.templateId, this.questionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.templateName = data.templateName;
          this.questionName = data.questionText;
          this.questionPrompts = this.parsePrompts(data.prompts);
          this.confidenceScore = data.averageConfidenceScore;
          this.locationResults = data.locationResults;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load question details:', err);
          this.isLoading = false;
          this.locationResults = [];
        }
      });
  }

  get filteredResults(): LocationResult[] {
    let result = this.locationResults;
    
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(r => 
        r.location.toLowerCase().includes(query) ||
        r.analysis.toLowerCase().includes(query) ||
        (r.locationNumber && r.locationNumber.toLowerCase().includes(query))
      );
    }
    
    result = [...result].sort((a, b) => {
      let comparison = 0;
      if (this.sortColumn === 'location') {
        comparison = a.location.localeCompare(b.location);
      } else if (this.sortColumn === 'meetsQualityStandards') {
        comparison = (a.meetsQualityStandards === b.meetsQualityStandards) ? 0 : a.meetsQualityStandards ? -1 : 1;
      } else if (this.sortColumn === 'score') {
        comparison = a.score - b.score;
      } else if (this.sortColumn === 'analysis') {
        comparison = a.analysis.localeCompare(b.analysis);
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }

  get locationCount(): number {
    return this.locationResults.length;
  }

  get highConfidenceCount(): number {
    return this.locationResults.filter(r => r.score >= 3).length;
  }

  get mediumConfidenceCount(): number {
    return this.locationResults.filter(r => r.score >= 2 && r.score < 3).length;
  }

  get lowConfidenceCount(): number {
    return this.locationResults.filter(r => r.score < 2).length;
  }

  get promptsCount(): number {
    return this.questionPrompts?.length || 0;
  }

  get aggregateScorePercent(): number {
    if (this.locationResults.length === 0) return 0;
    const totalScore = this.locationResults.reduce((sum, r) => sum + r.score, 0);
    const maxPossible = this.locationResults.length * 3;
    return (totalScore / maxPossible) * 100;
  }

  get totalScore(): number {
    return this.locationResults.reduce((sum, r) => sum + r.score, 0);
  }

  get maxScore(): number {
    return this.locationResults.length * 3;
  }

  toggleSort(column: SortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  formatScore(score: number): string {
    return score.toFixed(1);
  }

  getScoreColor(score: number): string {
    if (score >= 85) return '#10b981';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  }

  getScoreClass(score: number): string {
    if (score >= 3) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 2) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  }

  getItemScoreColor(score: number): string {
    if (score === 3) return '#10b981';
    if (score === 2) return '#f59e0b';
    return '#ef4444';
  }

  getItemDonutStyle(score: number): string {
    const color = this.getItemScoreColor(score);
    const percentage = (score / 3) * 100;
    return `conic-gradient(${color} ${percentage * 3.6}deg, #e5e7eb ${percentage * 3.6}deg)`;
  }

  getItemDonutStyleDark(score: number): string {
    const color = this.getItemScoreColor(score);
    const percentage = (score / 3) * 100;
    return `conic-gradient(${color} ${percentage * 3.6}deg, #374151 ${percentage * 3.6}deg)`;
  }

  getScorePercent(score: number): number {
    return Math.round((score / 3) * 100);
  }

  getQualityClass(meets: boolean): string {
    return meets 
      ? 'text-emerald-600 dark:text-emerald-400' 
      : 'text-red-600 dark:text-red-400';
  }

  getConfidenceLabel(score: number): string {
    if (score >= 85) return 'High';
    if (score >= 70) return 'Medium';
    return 'Low';
  }

  getItemConfidenceLabel(score: number): string {
    const percent = (score / 3) * 100;
    if (percent >= 85) return 'High';
    if (percent >= 70) return 'Medium';
    return 'Low';
  }

  viewImage(url: string | null) {
    if (url) {
      this.selectedImageUrl = url;
      this.fullImageRetries = 0;
      this.fullImageSrc = url;
    }
  }

  closeImageModal() {
    this.selectedImageUrl = null;
    this.fullImageSrc = null;
  }

  openDetailModal(item: LocationResult) {
    this.selectedItem = item;
    this.detailImageRetries = 0;
    this.detailImageSrc = item.imageUrl;
  }

  closeDetailModal() {
    this.selectedItem = null;
    this.detailImageSrc = null;
  }

  // The freshly-generated document URL can 404 for a brief window if the
  // backing blob is still being finalized right after analysis completes.
  // Retry with backoff (cache-busted so the browser re-requests instead of
  // reusing the failed response) instead of leaving a broken image icon.
  onDetailImageError() {
    const url = this.selectedItem?.imageUrl;
    if (!url || this.detailImageRetries >= this.maxImageRetries) return;
    this.detailImageRetries++;
    const attempt = this.detailImageRetries;
    setTimeout(() => { this.detailImageSrc = this.withRetryParam(url, attempt); }, 1000 * attempt);
  }

  onFullImageError() {
    const url = this.selectedImageUrl;
    if (!url || this.fullImageRetries >= this.maxImageRetries) return;
    this.fullImageRetries++;
    const attempt = this.fullImageRetries;
    setTimeout(() => { this.fullImageSrc = this.withRetryParam(url, attempt); }, 1000 * attempt);
  }

  private withRetryParam(url: string, attempt: number): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}retry=${attempt}`;
  }

  goBack() {
    this.router.navigate(['/analysis-report-questions'], {
      state: { 
        templateId: this.templateId,
        templateName: this.templateName
      }
    });
  }

  goToDocumentation() {
    this.router.navigate(['/documentation']);
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    localStorage.setItem('theme', theme);
    this.applyTheme(theme);
  }

  applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  initializeLucideIcons() {
    // Lucide icons initialization if needed
  }
}
